// Favre — karta produktu: widok 360° (film), galeria zdjęć, wybór rozmiaru/wariantu,
// zmiana koloru BEZ przeładowania (kolor = osobny produkt → Section Rendering API + podmiana w miejscu).
(() => {
  if (!document.querySelector('[data-fp]')) return;

  /* ---------- tło = kolor tła zdjęcia (średnia z rogów) ----------
     Zdjęcia mają własne szare tło w różnych odcieniach. Pole dostaje ten sam kolor, a całość
     (tło + zdjęcie) pojawia się RAZEM dopiero po wczytaniu i zmierzeniu (klasa is-ready). */
  const kolorTla = (img) => {
    try {
      const c = document.createElement('canvas');
      c.width = 8;
      c.height = 8;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, 8, 8);
      const rogi = [[0, 0], [7, 0], [0, 7], [7, 7]].map(([x, y]) => ctx.getImageData(x, y, 1, 1).data);
      const sr = (k) => Math.round(rogi.reduce((a, d) => a + d[k], 0) / rogi.length);
      return `rgb(${sr(0)}, ${sr(1)}, ${sr(2)})`;
    } catch (e) {
      return null; // obraz z obcej domeny
    }
  };

  // średni kolor lewej i prawej krawędzi (środkowa połowa wysokości) — obraz albo klatka filmu
  const kolorKrawedzi = (el) => {
    try {
      const N = 32;
      const c = document.createElement('canvas');
      c.width = N;
      c.height = N;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(el, 0, 0, N, N);
      const sum = [0, 0, 0];
      let n = 0;
      [0, N - 1].forEach((x) => {
        const d = ctx.getImageData(x, N / 4, 1, N / 2).data;
        for (let i = 0; i < d.length; i += 4) { sum[0] += d[i]; sum[1] += d[i + 1]; sum[2] += d[i + 2]; n++; }
      });
      const sr = sum.map((v) => Math.round(v / n));
      // Safari na iPhonie potrafi oddać z filmu pustą (czarną) klatkę — wtedy pole robiło się czarne.
      // Tła packshotów są jasne, więc prawie czarny wynik = błędny odczyt (2026-10-20).
      if (sr[0] + sr[1] + sr[2] < 90) return null;
      return `rgb(${sr.join(', ')})`;
    } catch (e) {
      return null; // obce źródło bez CORS
    }
  };

  const odslon = (box) => {
    if (box.classList.contains('is-ready')) return;
    const img = box.querySelector('img');
    const gotowe = () => {
      const bg = img && img.naturalWidth ? kolorTla(img) : null;
      if (bg) {
        box.style.backgroundColor = bg;
        // pierwsze zdjęcie karty nadaje kolor całej sekcji (w tej samej klatce co pokazanie zdjęcia)
        const fp = box.closest('[data-fp]');
        if (fp && !box.closest('.fp__swap') && box === fp.querySelector('.fp__slide')) {
          const m = fp.querySelector('[data-fp-media]');
          if (m) m.dataset.bgFotos = bg;
          // w trybie 360° kolor pola = tło filmu (inny odcień niż zdjęcia — inaczej widać szew przy krawędzi filmu)
          if (!m || m.dataset.mode !== '360' || !m.dataset.bg360) fp.style.setProperty('--fp-bg', bg);
        }
      }
      box.classList.add('is-ready');
    };
    if (!img || (img.complete && img.naturalWidth)) return gotowe();
    img.addEventListener('load', gotowe, { once: true });
    img.addEventListener('error', gotowe, { once: true });
  };
  const odslonWszystko = (scope = document) => scope.querySelectorAll('[data-fp-bg]').forEach(odslon);

  let ctrl = null; // AbortController bieżącej karty (po podmianie koloru stare nasłuchiwacze znikają)

  /* =====================================================================
     init(root) — wszystko, co dotyczy jednej karty produktu
     ===================================================================== */
  function init(root) {
    ctrl?.abort();
    ctrl = new AbortController();
    const signal = ctrl.signal;

    /* ---------- wysokość nagłówka → panel i media pod nim ---------- */
    const header = document.querySelector('.section-header');
    const ustawTop = () => {
      const h = header ? Math.max(0, header.getBoundingClientRect().height) : 0;
      root.style.setProperty('--fp-top', `${Math.round(h)}px`);
    };
    ustawTop();
    window.addEventListener('resize', ustawTop, { passive: true, signal });

    const media = root.querySelector('[data-fp-media]');

    /* ---------- termin dostawy (data-fp-eta): czas Niemiec, dni robocze bez sobót i niedziel ----------
       W init(), bo zmiana koloru podmienia panel — liczone przy każdym wejściu i po każdej zmianie koloru. */
    const eta = root.querySelector('[data-fp-eta]');
    if (eta) {
      const cz = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).map((x) => [x.type, x.value]));
      const robocze = (d) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6;
      const d = new Date(Date.UTC(Number(cz.year), Number(cz.month) - 1, Number(cz.day), 12));
      const cutoff = Number(eta.dataset.cutoff);
      const przed = robocze(d) && Number(cz.hour) < cutoff;
      let ile = Number(przed ? eta.dataset.d1 : eta.dataset.d2);
      while (ile > 0) { d.setUTCDate(d.getUTCDate() + 1); if (robocze(d)) ile--; }
      const data = new Intl.DateTimeFormat(document.documentElement.lang || 'de', { timeZone: 'UTC', weekday: 'long', day: '2-digit', month: '2-digit' }).format(d);
      eta.textContent = (przed ? eta.dataset.lBefore : eta.dataset.lAfter).replace('§t', `${String(cutoff).padStart(2, '0')}:00`).replace('§d', data);
      eta.hidden = false;
    }

    /* ---------- przełącznik 360° / zdjęcia ---------- */
    const modeBtn = root.querySelector('[data-fp-mode]');
    const modeLabel = root.querySelector('[data-fp-mode-label]');
    const setMode = (mode) => {
      media.dataset.mode = mode;
      if (modeBtn) modeBtn.setAttribute('aria-pressed', mode === '360' ? 'true' : 'false');
      if (modeLabel) modeLabel.textContent = mode === '360' ? modeLabel.dataset.l360 : modeLabel.dataset.lfotos;
      if (mode === '360') viewer?.resume();
      else viewer?.pause();
      const bg = mode === '360' ? media.dataset.bg360 : media.dataset.bgFotos;
      if (bg) root.style.setProperty('--fp-bg', bg);
      // pasek miniatur pod filmem: zaznaczona 360° albo bieżące zdjęcie
      root.querySelector('[data-fp-show360]')?.setAttribute('aria-current', mode === '360' ? 'true' : 'false');
      if (mode === '360') root.querySelectorAll('[data-fp-foto]').forEach((b) => b.setAttribute('aria-current', 'false'));
    };
    modeBtn?.addEventListener('click', () => setMode(media.dataset.mode === '360' ? 'fotos' : '360'));

    /* ---------- galeria zdjęć ----------
       Desktop (mysz): zdjęcia ułożone jedno na drugim, zmiana = przenikanie z „osiadaniem” (2026-10-26, decyzja usera:
       bardziej ekskluzywnie niż przesuwanie w bok). Nowe zdjęcie jest dekodowane PRZED animacją (bez pustego mignięcia).
       Telefon / dotyk / reduced-motion: jak wcześniej — natywne przewijanie z przyciąganiem (palec). */
    const track = root.querySelector('[data-fp-track]');
    const idxEl = root.querySelector('[data-fp-idx]');
    const stripBtns = [...root.querySelectorAll('[data-fp-goto]')];
    const slides = track ? [...track.children] : [];
    const fade = !!track && slides.length > 1
      && matchMedia('(hover: hover) and (pointer: fine)').matches
      && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cur = 0;
    let zmiana = 0; // numer ostatniego żądania (szybkie klikanie: wygrywa ostatnie)
    const zaznacz = (i) => {
      if (idxEl) idxEl.textContent = String(i + 1);
      if (media.dataset.mode !== '360') stripBtns.forEach((b, k) => b.setAttribute('aria-current', k === i ? 'true' : 'false'));
    };
    const gotowe = (slide) => {
      const img = slide.querySelector('img');
      if (!img) return Promise.resolve();
      img.loading = 'eager';
      const dek = img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      return Promise.race([dek, new Promise((r) => setTimeout(r, 450))]);
    };
    if (fade) {
      track.classList.add('is-fade');
      slides.forEach((s, k) => {
        s.classList.toggle('is-current', k === 0);
        s.setAttribute('aria-hidden', k === 0 ? 'false' : 'true');
      });
      // pozostałe zdjęcia doczytywane w tle, żeby przenikanie startowało od razu
      const doczytaj = () => slides.slice(1).forEach((s) => { const im = s.querySelector('img'); if (im) im.loading = 'eager'; });
      if ('requestIdleCallback' in window) requestIdleCallback(doczytaj, { timeout: 2500 }); else setTimeout(doczytaj, 1200);
    }

    const goTo = (i, smooth = true) => {
      if (!track || !slides[i]) return;
      if (!fade) {
        track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        return;
      }
      if (i === cur) { zaznacz(i); return; }
      const nr = ++zmiana;
      const stare = slides[cur];
      const nowe = slides[i];
      track.style.setProperty('--fp-dir', i > cur ? '1' : '-1');
      cur = i;
      zaznacz(i);
      const pokaz = () => {
        if (nr !== zmiana) return;
        slides.forEach((s) => {
          s.classList.remove('is-leaving', 'no-anim');
          if (s !== nowe) s.classList.remove('is-current');
          s.setAttribute('aria-hidden', s === nowe ? 'false' : 'true');
        });
        if (!smooth) { nowe.classList.add('no-anim', 'is-current'); return; }
        stare.classList.add('is-leaving');
        // klatka przerwy: nowe zdjęcie startuje z pozycji początkowej (rozmyte, lekko powiększone)
        requestAnimationFrame(() => { if (nr === zmiana) nowe.classList.add('is-current'); });
      };
      gotowe(nowe).then(pokaz);
    };
    stripBtns.forEach((b) => b.addEventListener('click', () => {
      // miniatura zdjęcia w pasku pod filmem 360° → najpierw tryb zdjęć, potem od razu właściwe zdjęcie
      if (b.hasAttribute('data-fp-foto') && media.dataset.mode !== 'fotos') {
        setMode('fotos');
        goTo(+b.dataset.fpGoto, false);
        stripBtns.forEach((x) => x.setAttribute('aria-current', x === b ? 'true' : 'false'));
        return;
      }
      goTo(+b.dataset.fpGoto);
    }));
    root.querySelector('[data-fp-show360]')?.addEventListener('click', () => setMode('360'));
    // dla konfiguratora haftu (favre-stickerei.js): przejście na zdjęcie nr i (tryb zdjęć, gdy był 360)
    root.fpGoTo = (i, smooth = true) => {
      if (media && media.dataset.mode === '360') { setMode('fotos'); goTo(i, false); return; }
      goTo(i, smooth);
    };

    if (track) {
      const biezacy = () => (fade ? cur : Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
      if (!fade) {
        let raf = 0;
        track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => zaznacz(biezacy()));
          },
          { passive: true }
        );
      }

      // klawiatura
      track.addEventListener('keydown', (e) => {
        const i = biezacy();
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, i + 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, i - 1)); }
      });

      // przeciąganie myszą: w trybie przenikania = gest (w lewo/prawo o > 40 px → następne/poprzednie)
      let startX = 0, startLeft = 0, drag = false, moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || slides.length < 2) return;
        drag = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        if (!fade) track.scrollLeft = startLeft - dx;
      }, { signal });
      window.addEventListener('pointerup', (e) => {
        if (!drag) return;
        drag = false;
        track.classList.remove('is-dragging');
        if (fade) {
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 40) goTo(Math.max(0, Math.min(slides.length - 1, cur + (dx < 0 ? 1 : -1))));
          return;
        }
        goTo(Math.max(0, Math.min(slides.length - 1, biezacy())));
      }, { signal });
      track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    }

    /* ---------- widok 360° ----------
       Film obraca się sam w pętli; przeciągnięcie zatrzymuje i obraca ręką.
       W tle wycinamy klatki do JPEG-ów (płynne obracanie bez przewijania filmu);
       gdy CDN nie pozwala czytać pikseli (CORS), zostaje przewijanie samego filmu. */
    const box360 = root.querySelector('[data-fp-360]');
    const viewer = box360 ? create360(box360) : null;
    // kolor tła filmu 360° = środek lewej i prawej krawędzi kadru (tam film styka się z polem; rogi plakatu
    // bywają ciemniejsze od filmu) → pole w tym samym odcieniu, a krawędzie filmu wtapiają się (maska w CSS)
    const ustaw360 = (bg) => {
      if (!bg) return;
      media.dataset.bg360 = bg;
      if (media.dataset.mode === '360') root.style.setProperty('--fp-bg', bg);
    };
    // WEJŚCIE (2026-10-21): przy pierwszym wejściu widok 360° jest ukryty (CSS: html.js .fp__v360-view:not(.is-shown)),
    // aż zmierzymy tło z PRAWDZIWEJ klatki filmu — plakat Shopify ma inne, ciemniejsze tło (~207) niż film (~223),
    // a pole startuje z domyślnego #f2f2f2. Wcześniej pole zmieniało kolor 2× i było widać prostokąt filmu.
    // Teraz: pole przechodzi do koloru filmu, a film wchodzi przenikaniem w tej samej chwili.
    // Zapas po 2,5 s (brak autoodtwarzania, wolne łącze): plakat z tłem zmierzonym z plakatu (do siebie pasują).
    // Po zmianie koloru widok przychodzi już z is-shown (pobierz()), więc tam nic się nie zmienia.
    const view360 = box360?.querySelector('[data-fp-view]');
    const pokaz360 = () => { if (view360 && !view360.classList.contains('is-shown')) view360.classList.add('is-shown'); };
    const poster360 = box360?.querySelector('[data-fp-poster]');
    if (poster360) {
      const zmierz = () => {
        const bg = poster360.naturalWidth ? kolorKrawedzi(poster360) : null;
        if (!bg) return;
        media.dataset.bg360poster = bg;
        if (view360?.classList.contains('is-shown') && !media.dataset.bg360) ustaw360(bg);
      };
      if (poster360.complete && poster360.naturalWidth) zmierz();
      else poster360.addEventListener('load', zmierz, { once: true, signal });
    }
    const video360 = box360?.querySelector('[data-fp-video]');
    video360?.addEventListener('loadeddata', () => {
      if (poster360 && getComputedStyle(poster360).visibility !== 'hidden' && view360?.classList.contains('is-shown')) return;
      ustaw360(kolorKrawedzi(video360));
    }, { once: true, signal });
    if (view360 && !view360.classList.contains('is-shown')) {
      // 'playing' = plakat właśnie znika (create360), na ekranie jest już klatka filmu
      const wejdz = () => {
        ustaw360(kolorKrawedzi(video360));
        requestAnimationFrame(pokaz360);
      };
      video360?.addEventListener('playing', wejdz, { once: true, signal });
      video360?.addEventListener('seeked', wejdz, { once: true, signal });
      const zapas = setTimeout(() => {
        if (view360.classList.contains('is-shown')) return;
        // plakat wciąż na wierzchu (film nie ruszył) → pole w kolorze PLAKATU; inaczej w kolorze klatki filmu
        const plakatNaWierzchu = poster360 && getComputedStyle(poster360).visibility !== 'hidden';
        if (plakatNaWierzchu && media.dataset.bg360poster) ustaw360(media.dataset.bg360poster);
        else if (video360 && video360.readyState >= 2) ustaw360(kolorKrawedzi(video360));
        pokaz360();
      }, 2500);
      signal.addEventListener('abort', () => clearTimeout(zapas));
    }
    // tło filmu jaśnieje/ciemnieje w trakcie obrotu (218–224 w RGB) — pole podąża za nim co 0,4 s
    // (przejście koloru w CSS 0,45 s), także przy obracaniu ręką z wyciętych klatek
    const frame360 = box360?.querySelector('[data-fp-frame]');
    if (video360) {
      const timer = setInterval(() => {
        if (document.hidden || media.dataset.mode !== '360') return;
        const zrodlo = frame360 && !frame360.hidden && frame360.complete && frame360.naturalWidth ? frame360 : video360;
        if (zrodlo === video360 && video360.readyState < 2) return;
        if (zrodlo === video360 && poster360 && getComputedStyle(poster360).visibility !== 'hidden') return;
        const bg = kolorKrawedzi(zrodlo);
        if (bg && bg !== media.dataset.bg360) ustaw360(bg);
      }, 400);
      signal.addEventListener('abort', () => clearInterval(timer));
    }

    function create360(box) {
      const view = box.querySelector('[data-fp-view]');
      const video = box.querySelector('[data-fp-video]');
      const poster = box.querySelector('[data-fp-poster]');
      const frameImg = box.querySelector('[data-fp-frame]');
      const degEl = box.querySelector('[data-fp-deg]');
      const hint = box.querySelector('[data-fp-hint]');
      const strip = box.querySelector('[data-fp-strip]');
      // telefon/tablet (dotyk): film 720p z samymi klatkami kluczowymi (płynne przewijanie palcem)
      const dotyk = window.matchMedia('(pointer: coarse)').matches;
      const src = (dotyk && box.dataset.srcTel) || box.dataset.src;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Klatki = WSZYSTKIE klatki filmu (8 s × 30 kl./s ≈ 240, czyli co ~1,5°), nie stałe 72 kąty co 5° —
      // przy 72 jedna klatka przypadała na ~14 px ruchu myszy i obraz przeskakiwał (zgłoszenie usera 2026-10-19).
      const klatki = []; // [{ t: 0..1 (ułamek obrotu), u: objectURL }] posortowane po t
      let komplet = false; // pierwszy przebieg wycinania skończony
      let pos = 0; // 0..1 — pokazywany kąt
      let cel = 0; // kąt, do którego płynnie dociągamy (bez zawijania, żeby ruch przez 0° był ciągły)
      let pokazany = 0; // ciągły (bez zawijania) kąt pokazany
      let wygladzam = false;
      let dragging = false, startX = 0, startPos = 0;
      let auto = !reduce;
      let interacted = false;
      let loaded = false;

      const setDeg = (p) => {
        degEl.textContent = String(Math.round((((p % 1) + 1) % 1) * 360) % 360);
      };

      // najbliższa klatka do kąta p (z zawinięciem 359° ↔ 0°); zwraca [klatka, odległość]
      const najblizsza = (p) => {
        const n = klatki.length;
        if (!n) return [null, 1];
        let lo = 0, hi = n;
        while (lo < hi) { const m = (lo + hi) >> 1; if (klatki[m].t < p) lo = m + 1; else hi = m; }
        const a = klatki[lo % n], b2 = klatki[(lo - 1 + n) % n];
        const da = Math.min(Math.abs(a.t - p), 1 - Math.abs(a.t - p));
        const db = Math.min(Math.abs(b2.t - p), 1 - Math.abs(b2.t - p));
        return da <= db ? [a, da] : [b2, db];
      };

      const show = (p) => {
        pos = ((p % 1) + 1) % 1;
        setDeg(pos);
        const [k, odl] = najblizsza(pos);
        // gotowa klatka (w trakcie wycinania tylko bliska: ±15°), inaczej skok w filmie
        if (k && (komplet || odl <= 15 / 360)) {
          if (frameImg.src !== k.u) frameImg.src = k.u;
          frameImg.hidden = false;
          video.style.visibility = 'hidden';
        } else if (video.duration) {
          skocz(pos * video.duration);
        }
      };

      // Skok w filmie: Shopify koduje klatkę kluczową co 3 s, każdy skok dekoduje do 90 klatek —
      // jeden skok naraz, ostatnia pozycja wygrywa.
      let skokTrwa = false, skokCel = null;
      const skocz = (t) => {
        skokCel = t;
        if (skokTrwa) return;
        skokTrwa = true;
        const doCelu = skokCel;
        skokCel = null;
        frameImg.hidden = true;
        video.style.visibility = '';
        video.addEventListener('seeked', () => {
          skokTrwa = false;
          if (skokCel !== null) skocz(skokCel);
        }, { once: true });
        // fastSeek skacze do klatki kluczowej (u Shopify co 3 s = co ~135°) — na iPhonie obraz przeskakiwał;
        // na telefonie dokładne przewinięcie (film z samymi klatkami kluczowymi → 1 klatka do zdekodowania)
        if (video.fastSeek && !dotyk) video.fastSeek(doCelu);
        else video.currentTime = doCelu;
      };

      // pętla klatek ekranu: podczas filmu licznik stopni, przy obracaniu płynne doganianie celu
      const tick = () => {
        if (signal.aborted) return;
        if (!video.paused && video.duration) {
          pos = video.currentTime / video.duration;
          setDeg(pos);
        } else if (wygladzam) {
          const roznica = cel - pokazany;
          pokazany = Math.abs(roznica) < 0.0004 ? cel : pokazany + roznica * 0.35;
          show(pokazany);
          if (pokazany === cel && !dragging) wygladzam = false;
        }
        requestAnimationFrame(tick);
      };
      const obroc = (p) => {
        if (!wygladzam) pokazany = pos;
        cel = p;
        wygladzam = true;
      };

      const load = () => {
        if (loaded) return;
        loaded = true;
        video.src = src;
        video.preload = 'auto';
        // lżejsza wersja na telefon nie istnieje → pełna
        if (src !== box.dataset.src) {
          video.addEventListener('error', () => {
            video.src = box.dataset.src;
            if (auto) video.play().catch(() => {});
          }, { once: true, signal });
        }
        // Safari na iPhonie nie pobiera filmu, dopóki nie padnie play() — czekanie na loadeddata = film stał
        if (auto) video.play().catch(() => {});
        // pierwszy obrót bez pętli: po pełnych 360° pokazujemy pierwsze zdjęcie wybranego wariantu
        // (decyzja usera 2026-10-19); po powrocie do 360° film kręci się już w pętli
        if (auto) video.loop = false;
        video.addEventListener('ended', () => {
          video.loop = true;
          if (interacted || media.dataset.mode !== '360') return;
          pokazZdjecieWariantu();
        }, { once: true, signal });
        // obrócone palcem, zanim film miał dane → po wczytaniu pokaż właściwy kąt
        video.addEventListener('loadedmetadata', () => { if (interacted) show(pos); }, { once: true, signal });
        video.addEventListener(
          'loadeddata',
          () => {
            video.classList.add('is-ready');
            // plakat znika dopiero, gdy film naprawdę pokazał klatkę (iOS: loadeddata bywa przed obrazem,
            // a bez autoodtwarzania, np. w trybie oszczędzania energii, zostawał czarny prostokąt)
            if (poster) {
              const schowaj = () => { poster.style.visibility = 'hidden'; };
              video.addEventListener('playing', schowaj, { once: true, signal });
              video.addEventListener('seeked', schowaj, { once: true, signal });
            }
            if (auto) video.play().catch(() => {});
            requestAnimationFrame(tick);
            // telefon: bez wycinania klatek — sam film (klatki z płótna Safari pokazywał jaśniej niż film);
            // płynność daje film z samymi klatkami kluczowymi (data-src-tel, snippet favre-360-tel)
            if (!dotyk) extract().catch(() => {});
          },
          { once: true }
        );
      };

      async function extract() {
        const v = document.createElement('video');
        v.muted = true;
        v.playsInline = true;
        v.crossOrigin = 'anonymous';
        v.preload = 'auto';
        v.setAttribute('aria-hidden', 'true');
        // w DOM (niewidoczny), bo requestVideoFrameCallback działa tylko dla filmu, który się renderuje
        v.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1';
        v.src = src;
        document.body.appendChild(v);
        signal.addEventListener('abort', () => v.remove());
        try {
          await new Promise((ok, err) => {
            v.addEventListener('loadeddata', ok, { once: true });
            v.addEventListener('error', err, { once: true });
          });
          const w = Math.min(1080, v.videoWidth);
          const h = Math.round((v.videoHeight / v.videoWidth) * w);
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          const ctx = c.getContext('2d');
          const d = v.duration;
          const czekam = [];
          const wstaw = (t, u) => {
            let i = klatki.length;
            while (i > 0 && klatki[i - 1].t > t) i--;
            klatki.splice(i, 0, { t, u });
          };
          // ta sama klatka filmu już jest (odstęp < 1/4 klatki przy 30 kl./s)
          const jest = (t) => { const [k, odl] = najblizsza(t); return k && odl < 0.25 / (d * 30); };
          // toBlob kopiuje płótno w chwili wywołania — można od razu rysować następną klatkę
          // pierwsza klatka czarna = przeglądarka (Safari iOS) nie oddaje pikseli filmu → bez klatek, zostaje film
          let sprawdzona = false;
          const zlap = (t) => {
            ctx.drawImage(v, 0, 0, w, h);
            if (!sprawdzona) {
              const px = ctx.getImageData(0, h >> 1, 8, 1).data;
              let jasnosc = 0;
              for (let i = 0; i < px.length; i += 4) jasnosc += px[i] + px[i + 1] + px[i + 2];
              if (jasnosc / 8 < 90) throw new Error('czarna klatka');
              sprawdzona = true;
            }
            czekam.push(new Promise((ok) => c.toBlob((b) => { if (b) wstaw(t, URL.createObjectURL(b)); ok(); }, 'image/jpeg', 0.86)));
          };
          const przebieg = (tempo) => new Promise((done) => {
            v.currentTime = 0;
            v.playbackRate = tempo;
            const cb = (now, meta) => {
              if (signal.aborted) return done();
              const t = meta.mediaTime / d;
              try {
                if (t < 1 && !jest(t)) zlap(t);
              } catch (e) {
                return done(); // SecurityError = brak CORS → zostaje film
              }
              if (v.ended || meta.mediaTime >= d - 0.04) return done();
              v.requestVideoFrameCallback(cb);
            };
            v.requestVideoFrameCallback(cb);
            v.addEventListener('ended', done, { once: true });
            v.play().catch(done);
          });

          if ('requestVideoFrameCallback' in v) {
            // 1) szybko (3×): większość klatek w ~3 s; 2) wolniej (1,5×): uzupełnienie brakujących
            await przebieg(3);
            v.pause();
            await Promise.all(czekam);
            if (klatki.length < 24) return;
            komplet = true;
            dodajPasek();
            await przebieg(1.5);
            v.pause();
            await Promise.all(czekam);
          } else {
            // przeglądarka bez requestVideoFrameCallback — 72 kąty skokami
            for (let i = 0; i < 72; i++) {
              if (signal.aborted) return;
              await new Promise((ok) => {
                v.addEventListener('seeked', ok, { once: true });
                v.currentTime = Math.min(d - 0.05, (i / 72) * d);
              });
              zlap(i / 72);
            }
            await Promise.all(czekam);
            if (klatki.length < 72) return;
            komplet = true;
            dodajPasek();
          }
        } catch (e) {
          // brak CORS / błąd filmu → zostaje sam film
        } finally {
          v.remove();
        }
      }

      // pasek miniatur jak w referencji: 12 kątów co 30°
      function dodajPasek() {
        if (!strip || strip.children.length) return;
        for (let k = 0; k < 12; k++) {
          const [kl] = najblizsza(k / 12);
          if (!kl) continue;
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'fp__strip-btn';
          b.setAttribute('aria-label', `${k * 30}°`);
          b.innerHTML = `<img src="${kl.u}" alt="">`;
          b.addEventListener('click', () => {
            stopAuto();
            // najkrótsza droga do kąta k·30° (płynnie, przez 0° jeśli bliżej)
            const teraz = wygladzam ? cel : pos;
            const krok = ((((k / 12 - teraz) % 1) + 1.5) % 1) - 0.5;
            obroc(teraz + krok);
          });
          strip.appendChild(b);
        }
        strip.hidden = false;
      }

      // miniatura zdjęcia wybranego wariantu w pasku pod filmem (zapas: pierwsze zdjęcie)
      function pokazZdjecieWariantu() {
        let i = 0;
        try {
          const warianty = JSON.parse(root.querySelector('[data-fp-variants]')?.textContent || '[]');
          const id = Number(root.querySelector('[data-fp-variant-input]')?.value);
          const v = warianty.find((w) => w.id === id);
          if (v && v.media) {
            const k = slides.findIndex((sl) => sl.dataset.mediaId === String(v.media));
            if (k >= 0) i = k;
          }
        } catch (e) {}
        const b = root.querySelector(`[data-fp-foto][data-fp-goto="${i}"]`);
        if (b) b.click();
        else { setMode('fotos'); goTo(i, false); }
      }

      const stopAuto = () => {
        auto = false;
        // bez danych nie pauzujemy — przerwałoby play() z gestu, które na iPhonie dopiero pobiera film
        if (video.readyState >= 2) video.pause();
        if (!interacted) interacted = true;
      };

      // przeciąganie: pełna szerokość pola = jeden obrót; obraz dogania kursor płynnie (tick)
      view.addEventListener('pointerdown', (e) => {
        load();
        // iPhone bez autoodtwarzania (np. tryb oszczędzania energii): film nie ma jeszcze danych —
        // play() w geście dotyku je pobiera, potem pauza; obracanie działa od razu, obraz dochodzi po wczytaniu
        if (video.readyState < 2) {
          const pr = video.play();
          if (pr) pr.then(() => { if (!auto) video.pause(); }).catch(() => {});
        }
        const zFilmu = !video.paused && video.readyState >= 2;
        stopAuto();
        dragging = true;
        startX = e.clientX;
        startPos = wygladzam ? cel : zFilmu && video.duration ? video.currentTime / video.duration : pos;
        if (!wygladzam) pokazany = cel = startPos;
        view.classList.add('is-dragging');
        hint?.classList.add('is-active');
        try { view.setPointerCapture?.(e.pointerId); } catch (err) {}
      });
      view.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        obroc(startPos - (e.clientX - startX) / Math.max(320, view.clientWidth));
      });
      const end = () => {
        dragging = false;
        view.classList.remove('is-dragging');
        hint?.classList.remove('is-active');
      };
      view.addEventListener('pointerup', end);
      view.addEventListener('pointercancel', end);

      // Telefon: pole ma touch-action: pan-y (pionowy ruch przewija stronę), ale przy obracaniu palcem
      // strona i tak jeździła góra-dół z każdym krzywym ruchem. Gdy gest zaczyna się poziomo (pierwsze ~8 px),
      // blokujemy przewijanie aż do puszczenia palca; gest pionowy dalej przewija stronę (user 2026-10-20).
      let tX = 0, tY = 0, kierunek = '';
      view.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        tX = t.clientX;
        tY = t.clientY;
        kierunek = '';
      }, { passive: true, signal });
      view.addEventListener('touchmove', (e) => {
        if (e.touches.length > 1) return; // dwa palce = powiększanie strony
        const t = e.touches[0];
        if (!kierunek) {
          const dx = Math.abs(t.clientX - tX), dy = Math.abs(t.clientY - tY);
          if (dx < 8 && dy < 8) return;
          kierunek = dx >= dy ? 'poziomo' : 'pionowo';
        }
        if (kierunek === 'poziomo' && e.cancelable) e.preventDefault();
      }, { passive: false, signal });

      // klawiatura: strzałki obracają o 5°
      box.tabIndex = 0;
      box.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        load();
        stopAuto();
        obroc((wygladzam ? cel : pos) + (e.key === 'ArrowRight' ? 5 : -5) / 360);
      });

      // start: film ładuje się po wczytaniu strony i tylko gdy pole jest widoczne
      const start = () => {
        if (navigator.connection?.saveData) return;
        const io = new IntersectionObserver((en) => {
          if (en.some((x) => x.isIntersecting)) { io.disconnect(); load(); }
        });
        io.observe(box);
        signal.addEventListener('abort', () => io.disconnect());
      };
      // od razu (wcześniej po `load` = po wszystkich zdjęciach i skryptach aplikacji — film startował z opóźnieniem)
      start();
      // iOS bez autoodtwarzania: film rusza przy pierwszym dotknięciu strony (np. przewinięciu)
      let ruszyl = false;
      video.addEventListener('playing', () => { ruszyl = true; }, { once: true, signal });
      document.addEventListener('touchstart', () => {
        if (auto && loaded && !ruszyl && video.paused && media.dataset.mode === '360') video.play().catch(() => {});
      }, { once: true, passive: true, signal });

      signal.addEventListener('abort', () => {
        video.pause();
        klatki.forEach((k) => URL.revokeObjectURL(k.u));
      });

      return {
        pause: () => video.pause(),
        resume: () => { if (auto && loaded) video.play().catch(() => {}); },
      };
    }

    // cała strona, nie tylko karta: „Ähnliche Produkte” (favre-similar) też ma pola [data-fp-bg] — bez tego
    // przy pierwszym wejściu zostawały szare #f2f2f2 obok zdjęć z tłem #ddd (2026-10-22)
    odslonWszystko();

    /* ---------- kolory: nazwa przy etykiecie „Farbe:” podąża za kursorem ---------- */
    const farbeName = root.querySelector('[data-fp-farbe-name]');
    root.querySelectorAll('[data-fp-farbe] .fp__thumb').forEach((t) => {
      const pokaz = () => { farbeName.textContent = t.dataset.name; };
      const wroc = () => { farbeName.textContent = farbeName.dataset.current; };
      t.addEventListener('mouseenter', pokaz);
      t.addEventListener('focus', pokaz);
      t.addEventListener('mouseleave', wroc);
      t.addEventListener('blur', wroc);

      // zmiana koloru w miejscu; dane pobieramy już przy najechaniu
      t.addEventListener('mouseenter', () => pobierz(t.href).catch(() => {}), { once: true });
      t.addEventListener('touchstart', () => pobierz(t.href).catch(() => {}), { once: true, passive: true });
      t.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        if (t.classList.contains('is-active')) return;
        zmienKolor(t.href, true);
      });
    });

    /* ---------- warianty ---------- */
    const dataEl = root.querySelector('[data-fp-variants]');
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    const input = root.querySelector('[data-fp-variant-input]');
    const priceEl = root.querySelector('[data-fp-price]');
    const compareEl = root.querySelector('[data-fp-compare]');
    const addBtn = root.querySelector('[data-fp-add]');
    const dimsEl = root.querySelector('[data-fp-dims]');
    const optionRows = [...root.querySelectorAll('[data-fp-option]')];

    const selected = () =>
      optionRows.map((row) => row.querySelector('.fp__pill.is-active')?.dataset.value ?? null);

    const findVariant = (opts) =>
      variants.find((v) => v.options.every((o, i) => opts[i] == null || o === opts[i]));

    const dims = (value) => {
      const cz = value.split(' ');
      if (cz.length < 2) return '';
      return `${value.slice(cz[0].length).replace(/cm/g, '').replace(/x/g, ' × ').replace(/\s+/g, ' ').trim()} cm`;
    };

    const markAvailability = () => {
      const opts = selected();
      optionRows.forEach((row, i) => {
        row.querySelectorAll('.fp__pill').forEach((pill) => {
          const test = [...opts];
          test[i] = pill.dataset.value;
          const v = findVariant(test);
          pill.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    };

    const apply = () => {
      const v = findVariant(selected());
      if (!v) return;
      input.value = v.id;
      priceEl.textContent = v.price;
      if (compareEl) {
        compareEl.hidden = !v.compare;
        compareEl.textContent = v.compare || '';
      }
      addBtn.disabled = !v.available;
      addBtn.removeAttribute('aria-disabled');
      addBtn.querySelector('span').textContent = v.available ? addBtn.dataset.lAdd : addBtn.dataset.lSold;
      if (dimsEl && optionRows[0]) {
        const val = optionRows[0].querySelector('.fp__pill.is-active')?.dataset.value || '';
        dimsEl.textContent = dims(val) ? ` ${dims(val)}` : '';
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url);
      if (v.media) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(v.media));
        if (i >= 0) goTo(i);
      }
      markAvailability();
      root.dispatchEvent(new CustomEvent('favre:variant', { bubbles: true, detail: { variant: v } }));
    };

    optionRows.forEach((row) => {
      row.addEventListener('click', (e) => {
        const pill = e.target.closest('.fp__pill');
        if (!pill) return;
        row.querySelectorAll('.fp__pill').forEach((p) => {
          const on = p === pill;
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        apply();
      });
    });
    markAvailability();
    root.dispatchEvent(new CustomEvent('favre:init', { bubbles: true }));

    return { selected };
  }

  /* =====================================================================
     Zmiana koloru bez przeładowania
     ===================================================================== */
  const cache = new Map();

  // sekcje strony produktu, które zależą od produktu (karta, Eigenschaften, Ähnliche Produkte)
  const sekcje = () =>
    [...document.querySelectorAll('.shopify-section')]
      .filter((s) => s.querySelector('[data-fp], .ff, .fs'))
      .map((s) => s.id.replace('shopify-section-', ''));

  const idGlownej = () =>
    document.querySelector('[data-fp]').closest('.shopify-section').id.replace('shopify-section-', '');

  function pobierz(href) {
    const url = new URL(href, location.origin);
    const klucz = url.pathname;
    if (cache.has(klucz)) return cache.get(klucz);
    const ids = sekcje();
    const glowna = idGlownej();
    const obietnica = fetch(`${url.pathname}?sections=${ids.join(',')}`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(async (json) => {
        // zapas: cała strona (np. lokalny podgląd CLI oddaje puste sekcje) → wycinamy sekcje z HTML
        if (!json[glowna]) {
          const r = await fetch(url.pathname);
          if (!r.ok) throw new Error(String(r.status));
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          json = {};
          ids.forEach((id) => {
            const el = doc.getElementById(`shopify-section-${id}`);
            if (el) json[id] = el.outerHTML;
          });
          if (!json[glowna]) throw new Error('brak sekcji');
        }
        // zdjęcie wczytujemy i mierzymy ZANIM je pokażemy — tło i zdjęcie wejdą razem
        const tmp = document.createElement('div');
        tmp.innerHTML = json[glowna];
        const fig = tmp.querySelector('.fp__slide[data-fp-bg]');
        const img = fig?.querySelector('img');
        if (img) {
          const pre = new Image();
          if (img.sizes) pre.sizes = img.sizes;
          if (img.srcset) pre.srcset = img.srcset;
          pre.src = img.src;
          await pre.decode().catch(() => {});
          const bg = pre.naturalWidth ? kolorTla(pre) : null;
          if (bg) fig.style.backgroundColor = bg;
          fig.classList.add('is-ready');
          img.loading = 'eager';
          const m = tmp.querySelector('[data-fp-media]');
          if (m && bg) m.dataset.bgFotos = bg;
        }
        const plakat = tmp.querySelector('[data-fp-poster]');
        if (plakat) {
          const pre = new Image();
          pre.src = plakat.currentSrc || plakat.src;
          await pre.decode().catch(() => {});
          const bg = pre.naturalWidth ? kolorKrawedzi(pre) : null;
          const m = tmp.querySelector('[data-fp-media]');
          if (bg && m) m.dataset.bg360 = bg;
        }
        // przy zmianie koloru widok 360° wchodzi razem z warstwą (przenikanie warstwy), bez czekania na film
        tmp.querySelectorAll('[data-fp-view]').forEach((v) => v.classList.add('is-shown'));
        return { html: tmp.innerHTML, json, glowna };
      })
      .catch((e) => {
        cache.delete(klucz);
        throw e;
      });
    cache.set(klucz, obietnica);
    return obietnica;
  }

  let api = null;
  let trwa = false;

  async function zmienKolor(href, push) {
    if (trwa) return;
    trwa = true;
    const root = document.querySelector('[data-fp]');
    root.classList.add('is-switching');

    let dane;
    try {
      dane = await pobierz(href);
    } catch (e) {
      location.href = href; // awaryjnie zwykłe przejście
      return;
    }
    const { html, json, glowna } = dane;
    const poprzedni = api?.selected?.() || [];
    const staryTytul = root.querySelector('.fp__title')?.textContent.trim();

    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const nowy = tmp.querySelector('[data-fp]');
    // ten sam rozmiar w nowym kolorze ustawiony W NOWYM PANELU, zanim trafi na stronę
    // (wcześniej panel wchodził z domyślnym XS, a XL zaznaczał się dopiero po animacji zdjęcia)
    const wariantPoZmianie = ustawRozmiar(nowy, poprzedni);

    // panel od razu; nowe zdjęcie (już z tłem) przenika się nad starym.
    // Przenosimy PRAWDZIWE węzły (bez klonów i drugiej podmiany) — nic nie mignie.
    root.querySelector('.fp__panel-wrap').replaceWith(nowy.querySelector('.fp__panel-wrap'));
    const staraMedia = root.querySelector('[data-fp-media]');
    const warstwa = nowy.querySelector('[data-fp-media]');
    warstwa.removeAttribute('data-fp-media');
    warstwa.classList.add('fp__swap');
    staraMedia.appendChild(warstwa);
    // tło sekcji przechodzi do koloru nowego zdjęcia razem z przenikaniem
    const noweTlo = (warstwa.dataset.mode === '360' && warstwa.dataset.bg360) || warstwa.dataset.bgFotos || warstwa.querySelector('.fp__slide')?.style.backgroundColor;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    await new Promise((ok) => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          warstwa.classList.add('is-in');
          if (noweTlo) root.style.setProperty('--fp-bg', noweTlo);
        })
      );
      if (reduce) return setTimeout(ok, 30);
      warstwa.addEventListener('transitionend', (e) => { if (e.target === warstwa) ok(); });
      setTimeout(ok, 700);
    });

    // warstwa staje się polem mediów (ten sam węzeł — zdjęcie się nie przeładowuje)
    warstwa.classList.remove('fp__swap', 'is-in');
    warstwa.setAttribute('data-fp-media', '');
    staraMedia.replaceWith(warstwa);
    // dane wariantów + JSON-LD nowego produktu
    root.querySelectorAll('script[type="application/json"], script[type="application/ld+json"]').forEach((s) => s.remove());
    nowy.querySelectorAll('script[type="application/json"], script[type="application/ld+json"]').forEach((s) => root.appendChild(s));
    root.dataset.productUrl = nowy.dataset.productUrl || '';
    root.classList.remove('is-switching');

    // Eigenschaften / Ähnliche Produkte nowego produktu
    Object.entries(json).forEach(([id, sekcjaHtml]) => {
      if (id === glowna || !sekcjaHtml) return;
      const el = document.getElementById(`shopify-section-${id}`);
      if (el) el.outerHTML = sekcjaHtml;
    });
    odslonWszystko();

    const url = new URL(href, location.origin);
    url.searchParams.delete('variant');
    if (push) history.pushState({ fp: true }, '', url);
    const nowyTytul = root.querySelector('.fp__title')?.textContent.trim();
    if (staryTytul && nowyTytul) document.title = document.title.replace(staryTytul, nowyTytul);

    api = init(root);
    if (wariantPoZmianie) {
      const u = new URL(location.href);
      u.searchParams.set('variant', wariantPoZmianie);
      history.replaceState(history.state, '', u);
    }
    trwa = false;
  }

  // Zaznacza w panelu nowego koloru ten sam rozmiar (po kodzie XS/S/M…, bo wartości różnią się „cm”)
  // i od razu ustawia wariant, cenę, wymiary i przycisk — zwraca id wariantu albo null.
  function ustawRozmiar(nowy, poprzedni) {
    if (!poprzedni.some(Boolean)) return null;
    poprzedni.forEach((wart, i) => {
      if (!wart) return;
      const kod = wart.split(' ')[0];
      const pille = [...nowy.querySelectorAll(`[data-fp-option="${i}"] .fp__pill`)];
      const cel = pille.find((p) => p.dataset.value.split(' ')[0] === kod);
      if (!cel) return;
      pille.forEach((p) => {
        p.classList.toggle('is-active', p === cel);
        p.setAttribute('aria-checked', p === cel ? 'true' : 'false');
      });
    });
    let warianty = [];
    try { warianty = JSON.parse(nowy.querySelector('[data-fp-variants]')?.textContent || '[]'); } catch (e) {}
    const opcje = [...nowy.querySelectorAll('[data-fp-option]')].map((r) => r.querySelector('.fp__pill.is-active')?.dataset.value ?? null);
    const v = warianty.find((w) => w.options.every((o, i) => opcje[i] == null || o === opcje[i]));
    if (!v) return null;
    const input = nowy.querySelector('[data-fp-variant-input]');
    if (input) input.value = v.id;
    const cena = nowy.querySelector('[data-fp-price]');
    if (cena) cena.textContent = v.price;
    const stara = nowy.querySelector('[data-fp-compare]');
    if (stara) { stara.hidden = !v.compare; stara.textContent = v.compare || ''; }
    const dodaj = nowy.querySelector('[data-fp-add]');
    if (dodaj) {
      dodaj.disabled = !v.available;
      const t = dodaj.querySelector('span');
      if (t) t.textContent = v.available ? dodaj.dataset.lAdd : dodaj.dataset.lSold;
    }
    const wym = nowy.querySelector('[data-fp-dims]');
    const w0 = opcje[0];
    if (wym && w0) {
      const cz = w0.split(' ');
      wym.textContent = cz.length > 1 ? ` ${w0.slice(cz[0].length).replace(/cm/g, '').replace(/x/g, ' × ').replace(/\s+/g, ' ').trim()} cm` : '';
    }
    return v.id;
  }

  // wstecz / dalej w przeglądarce
  window.addEventListener('popstate', () => {
    const root = document.querySelector('[data-fp]');
    if (!root) return;
    const tu = location.pathname;
    const aktywny = root.querySelector('[data-fp-farbe] .fp__thumb.is-active');
    if (aktywny && new URL(aktywny.href, location.origin).pathname === tu) return;
    zmienKolor(location.href, false);
  });

  api = init(document.querySelector('[data-fp]'));
})();
